import { Injectable } from '@angular/core';
import {
  FeelsLikeColorCelsiusRules,
  FeelsLikeColorFahrenheitRules,
  PrecipColorInchesRules,
  PrecipColorMmRules,
  TemperatureColorCelsiusRules,
  TemperatureColorFahrenheitRules,
} from '@firebird-web/shared-constants';
import { ColorCodeEnum } from 'libs/shared/enums';

@Injectable({
  providedIn: 'root',
})
export class ColorCodeService {
  public getTemperatureColorCode(unit: string, value?: number): ColorCodeEnum {
    if (value == null) return ColorCodeEnum.Default;

    return unit === 'C'
      ? this.getTemperatureColorCelsius(value)
      : this.getTemperatureColorFahrenheit(value);
  }

  public getFeelsLikeColorCode(unit: string, value?: number): ColorCodeEnum {
    if (value == null) return ColorCodeEnum.Default;

    return unit === 'C'
      ? this.getFeelsLikeColorCelsius(value)
      : this.getFeelsLikeColorFahrenheit(value);
  }

  public getPrecipColorCode(unit: string, value?: number): ColorCodeEnum {
    if (value == null) return ColorCodeEnum.Default;

    return unit === 'mm'
      ? this.getPrecipColorMm(value)
      : this.getPrecipColorInches(value);
  }

  public getPopColorCode(value?: number): ColorCodeEnum {
    if (value == null) return ColorCodeEnum.Default;
    if (value < 20) {
      return ColorCodeEnum.DiffFromPrevPopSmall;
    }

    if (value < 50) {
      return ColorCodeEnum.DiffFromPrevPopMedium;
    }
    if (value <= 100) {
      return ColorCodeEnum.DiffFromPrevPopLarge;
    }
    return ColorCodeEnum.Default;
  }

  public getPrecipDifferences(value?: number): ColorCodeEnum {
    if (value == null) return ColorCodeEnum.Default;
    if (value < 0) {
      return ColorCodeEnum.PrecipAmountDiffRange1;
    }
    if (value > 0) {
      return ColorCodeEnum.PrecipAmountDiffRange2;
    }

    return ColorCodeEnum.Default;
  }

  public getPopDifferences(value?: number): ColorCodeEnum {
    if (value == null) return ColorCodeEnum.Default;
    if (value >= 1) {
      return ColorCodeEnum.POPDiffAboveZero;
    }
    if (value <= -1) {
      return ColorCodeEnum.POPDiffBelowZero;
    }
    return ColorCodeEnum.Default;
  }

  public getDifferencesColorCode(value: number): string {
    if (value === -999) {
      return '';
    }
    if (value > 0) {
      return ColorCodeEnum.DiffValAboveZero;
    }
    if (value < 0) {
      return ColorCodeEnum.DiffValBelowZero;
    }
    return '';
  }

  public getFreshLookDifferenceColorCode(value: number): ColorCodeEnum {
    if (value >= 1) {
      return ColorCodeEnum.DiffAboveZero;
    }
    if (value <= -1) {
      return ColorCodeEnum.DiffBelowZero;
    }
    return ColorCodeEnum.Default;
  }

  private getTemperatureColorCelsius(value: number): ColorCodeEnum {
    return (
      TemperatureColorCelsiusRules.find((r) => value > r.min && value <= r.max)
        ?.color ?? ColorCodeEnum.DiffFromNormalSA
    );
  }

  private getTemperatureColorFahrenheit(value: number): ColorCodeEnum {
    if (value < 0) {
      return (
        TemperatureColorFahrenheitRules.find(
          (r) => value > r.min && value <= r.max
        )?.color ?? ColorCodeEnum.DiffFromNormalSA
      );
    } else {
      return (
        TemperatureColorFahrenheitRules.find(
          (r) => value >= r.min && value < r.max
        )?.color ?? ColorCodeEnum.DiffFromNormalSA
      );
    }
  }

  private getFeelsLikeColorCelsius(value: number): ColorCodeEnum {
    return (
      FeelsLikeColorCelsiusRules.find((r) => value > r.min && value <= r.max)
        ?.color ?? ColorCodeEnum.Default
    );
  }

  private getFeelsLikeColorFahrenheit(value: number): ColorCodeEnum {
    if (value < 0) {
      return (
        FeelsLikeColorFahrenheitRules.find(
          (r) => value > r.min && value <= r.max
        )?.color ?? ColorCodeEnum.Default
      );
    } else {
      return (
        FeelsLikeColorFahrenheitRules.find(
          (r) => value >= r.min && value < r.max
        )?.color ?? ColorCodeEnum.Default
      );
    }
  }

  public getPrecipColorMm(value: number) {
    return (
      PrecipColorMmRules.find((r) => value > r.min && value <= r.max)?.color ??
      ColorCodeEnum.Default
    );
  }

  public getPrecipColorInches(value: number) {
    if (value <= 0.5) {
      return (
        PrecipColorInchesRules.find((r) => value > r.min && value <= r.max)
          ?.color ?? ColorCodeEnum.Default
      );
    } else {
      return (
        PrecipColorInchesRules.find((r) => value >= r.min && value < r.max)
          ?.color ?? ColorCodeEnum.Default
      );
    }
  }
}
